import React, { useEffect, useRef } from 'react';
import {
  Action,
  AuthType,
  init,
  LiveboardEmbed as TSLiveboardEmbed,
} from '@thoughtspot/visual-embed-sdk';
import BEM from 'common/bem';

const classes = BEM.with('ClinicalWorkflows');

type Props = {
  liveboardId: string;
  host: string;
  token: string;
  // ThoughtSpot integer org ID — required in multi-org setups to skip the org selector
  orgId: number;
};

/**
 * Renders a ThoughtSpot liveboard as a full-height iframe.
 * The SDK is initialized once per host/token pair; subsequent renders reuse it.
 */
export default function LiveboardEmbed({ liveboardId, host, token, orgId }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Track the last initialized host so we only re-init when it changes
  const initializedHostRef = useRef<string | null>(null);
  // Always holds the latest token so the getAuthToken closure never goes stale
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!containerRef.current) return;

    if (initializedHostRef.current !== host) {
      init({
        thoughtSpotHost: host,
        authType: AuthType.TrustedAuthTokenCookieless,
        getAuthToken: () => Promise.resolve(tokenRef.current),
      });
      initializedHostRef.current = host;
    }

    const embed = new TSLiveboardEmbed(containerRef.current, {
      liveboardId,
      fullHeight: true,
      overrideOrgId: orgId,
      // Hide the liveboard-level header (title, AI Highlights, share, actions menu)
      hideLiveboardHeader: true,
      hiddenActions: [
        Action.Edit,
        Action.RenameModalTitleDescription,
        Action.ShowUnderlyingData,
        Action.Download,
        Action.CreateMonitor,
        Action.ManageMonitor,
        Action.SpotIQAnalyze,
      ],
    });

    embed.render();

    // Destroy the embed on cleanup to avoid stale iframes when switching liveboards
    return () => {
      embed.destroy();
    };
  }, [host, liveboardId, orgId]);

  return <div ref={containerRef} className={classes('liveboard-embed')} />;
}
